import { LoadingButton, Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import { Autocomplete, Button, Chip, Dialog, MenuItem, OutlinedInput, Skeleton, TextField } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { BarChartSquarePlus, Pencil01, Route } from "@untitled-ui/icons-react/build/esm";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useBoolean } from "usehooks-ts";
import PlusIcon from "@untitled-ui/icons-react/build/esm/Plus";
import { CustomFieldRenderer, CustomFieldsDialog } from "~/components/custom-fields-dialog";
import { ProductsTableSummary, TableItem, TableItemDisplay, TableSubtotalAddons } from "~/components/products-table-summary";
import { useAppRouter } from "~/hooks/use-app-router";
import { useTillSummary } from "~/hooks/use-till-summary";
import { CreateFileVariables, useCreateFile } from "~/queries/useCreateFile";
import { useGetAgents } from "~/queries/useGetAgents";
import { useGetClients } from "~/queries/useGetClients";
import { useGetCounters } from "~/queries/useGetCounters";
import { useGetCurrentCompanyCache } from "~/queries/useGetCurrentCompany";
import { useGetFileCustomFields } from "~/queries/useGetCustomFields";
import { useGetProcessesCache } from "~/queries/useGetProcesses";
import { useGetProductOptions } from "~/queries/useGetProductOptions";
import { useGetProducts } from "~/queries/useGetProducts";
import { useGetUsers } from "~/queries/useGetUsers";
import { useMeCache } from "~/queries/useMe";
import { useResetTill, useSetTillIsOpen, useTill, useUpdateTillCreateFileInput } from "~/store";
import { CounterConfigsInput, CUSTOM_FIELD_MODEL, CUSTOM_FIELD_TYPE, CustomFieldValueInput, ROLE } from "~/types/__generated/gql/graphql";
import { TillFileInput } from "~/types/store";
import { DefineProcessPathsDialog } from "./define-process-paths-dialog";
import { paths } from "~/paths";
import { PostAdd } from "@mui/icons-material";
import { COUNTER_TYPE } from "~served/configs";
import { DateTimePicker } from "@mui/x-date-pickers";
export const CreateFileModal = () => {
  const {
    appQuery,
    currentActions: {
      isCreatingFile
    },
    router,
    goToFileDetailsOrderSheetQuery
  } = useAppRouter();
  const {
    data: company
  } = useGetCurrentCompanyCache();
  const {
    data: me
  } = useMeCache();
  const {
    data: processes = []
  } = useGetProcessesCache();
  const setTillIsOpen = useSetTillIsOpen();
  const till = useTill();
  const resetTill = useResetTill();
  const updateCreateFileInput = useUpdateTillCreateFileInput();
  const createFile = useCreateFile();
  const {
    data: agents = [],
    isLoading: isAgentsLoading
  } = useGetAgents();
  const {
    data: clients = [],
    isLoading: isClientsLoading
  } = useGetClients();
  const {
    createFileInputSummary,
    tillItemsSummary
  } = useTillSummary();
  const {
    data: users = [],
    isLoading: isUsersLoading
  } = useGetUsers();
  const {
    data: counters = [],
    isLoading: isCountersLoading
  } = useGetCounters();
  const {
    value: isProcessPathsDialogOpen,
    setTrue: openProcessPathsDialog,
    setFalse: closeProcessPathsDialog
  } = useBoolean(false);
  const {
    value: isCustomFieldsDialogOpen,
    setTrue: openCustomFieldsDialog,
    setFalse: closeCustomFieldsDialog
  } = useBoolean(false);
  const {
    data: customFields = []
  } = useGetFileCustomFields();
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, CustomFieldValueInput>>({});
  const [searchClient, setSearchClient] = useState<string>();
  const {
    data: products = []
  } = useGetProducts();
  const {
    data: productOptions = []
  } = useGetProductOptions();
  const onClose = async () => await router.push(paths.index);
  const handleCreateFileInColumn = async (columnId: string): Promise<void> => {
    if (columnId === "") toast.error(`No process found.`);
    const newFile = await createFile.mutateAsync({
      input: {
        ...till.createFileInput!,
        products: createFileInputSummary.products as CreateFileVariables["input"]["products"],
        cancelledProducts: createFileInputSummary.cancelledProducts as CreateFileVariables["input"]["cancelledProducts"],
        process: columnId,
        sortingIndex: processes.find(process => process._id === columnId)?._files?.length ?? 0,
        customFields: Object.values(customFieldValues)
      }
    });
    toast.success(`New ${company?.fileAlias?.singular} created`);
    void goToFileDetailsOrderSheetQuery(newFile._id);
  };
  useEffect(() => {
    if (isCreatingFile) {
      const fileCounter = counters.find(c => (c.type === COUNTER_TYPE.FILE && c.isDefault) === true)?._id ?? null;
      const invoiceCounter = counters.find(c => (c.type === COUNTER_TYPE.INVOICE && c.isDefault) === true)?._id ?? null;
      updateCreateFileInput({
        assignees: till.createFileInput?.assignees && till.createFileInput?.assignees.length > 0 ? till.createFileInput?.assignees : [],
        client: appQuery.clientId ?? undefined,
        agent: till.createFileInput?.agent ?? undefined,
        cancelledProducts: [],
        process: appQuery.processId,
        products: [],
        processPaths: [],
        isCancelled: false,
        isProcessed: false,
        customFields: [],
        counterConfigs: fileCounter || invoiceCounter ? {
          file: fileCounter ? {
            counter: fileCounter
          } as unknown as CounterConfigsInput : undefined,
          invoice: invoiceCounter ? {
            counter: invoiceCounter
          } as unknown as CounterConfigsInput : undefined
        } : null,
        issueDate: new Date().toISOString(),
        dueDate: null
      });
    }
    return () => {
      if (isCreatingFile) {
        if (router.pathname !== "/clients/create") {
          resetTill();
          setCustomFieldValues({});
        }
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingFile, appQuery.processId, appQuery.clientId, isCountersLoading]);
  const productsTableItems = useMemo<TableItem[]>(() => createFileInputSummary.products.map(product => ({
    id: product._id.toString(),
    name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
    quantity: product.quantity,
    subtotal: product.subtotal,
    options: product.options.map(option => ({
      id: option._id.toString(),
      name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
      quantity: option.quantity,
      subtotal: option.subtotal
    })),
    subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
  })), [createFileInputSummary.products, productOptions, products]);
  const fileItemsDisplay = useMemo<TableItemDisplay>(() => ({
    prev: tillItemsSummary.prev.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    })),
    add: tillItemsSummary.add.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    })),
    remove: tillItemsSummary.remove.products.map(product => ({
      id: product._id.toString(),
      name: products.find(p => p._id === product.product)?.name ?? "[UNKNOWN PRODUCT]",
      quantity: product.quantity,
      subtotal: product.subtotal,
      options: product.options.map(option => ({
        id: option._id.toString(),
        name: productOptions.find(p => p._id === option.option)?.name ?? "[UNKNOWN PRODUCT OPTION]",
        quantity: option.quantity,
        subtotal: option.subtotal
      })),
      subtotalAddons: product.subtotalAddons as unknown as TableSubtotalAddons
    }))
  }), [productOptions, products, tillItemsSummary.add.products, tillItemsSummary.prev.products, tillItemsSummary.remove.products]);
  const isLoading = isAgentsLoading || isClientsLoading || isUsersLoading || isCountersLoading || !till.createFileInput;
  return <Dialog open fullWidth maxWidth="xl" scroll="paper" data-sentry-element="Dialog" data-sentry-component="CreateFileModal" data-sentry-source-file="create-file-modal.tsx">
			{isCustomFieldsDialogOpen && <CustomFieldsDialog model={CUSTOM_FIELD_MODEL.FILE} onClose={closeCustomFieldsDialog} />}

			<Stack gap={3} p={3} sx={{
      height: "100dvh",
      overflow: "auto"
    }} data-sentry-element="Stack" data-sentry-source-file="create-file-modal.tsx">
				{isLoading ? <>
						<Stack direction="row" justifyContent="space-between" spacing={1}>
							<Skeleton variant="rounded" height={40} sx={{
            flex: 1
          }} />
							<Skeleton variant="circular" height={40} width={40} />
						</Stack>
						<Box>
							<Skeleton variant="rounded" height={40} />
						</Box>
						<Box>
							<Grid container spacing={3}>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="circular" height={40} width={40} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Skeleton variant="rounded" height={40} width={120} />
								</Grid>
								<Grid xs={12} sm={8}>
									<Skeleton variant="rounded" height={40} sx={{
                flex: 1
              }} />
								</Grid>
								<Grid xs={12}>
									<Skeleton variant="rounded" height={240} sx={{
                flex: 1
              }} />
								</Grid>
							</Grid>
						</Box>
					</> : <>
						{isProcessPathsDialogOpen && <DefineProcessPathsDialog initialValues={till.createFileInput?.processPaths ?? []} onDone={values => {
          updateCreateFileInput({
            processPaths: values as TillFileInput["processPaths"]
          });
          closeProcessPathsDialog();
        }} onCancel={closeProcessPathsDialog} />}
						<Box>
							<Grid container spacing={2}>
								<Grid xs={11} md={11}>
									<Stack spacing={2} direction="row">
										<TextField select label={`${company?.fileAlias.singular ?? "File"} Prefix`} sx={{
                  width: "320px"
                }} value={till.createFileInput?.counterConfigs?.file ? till.createFileInput?.counterConfigs?.file.counter : null} onChange={e => {
                  updateCreateFileInput({
                    counterConfigs: {
                      ...till.createFileInput?.counterConfigs,
                      file: {
                        counter: e.target.value
                      }
                    }
                  });
                }}>
											{counters.filter(i => i.type === COUNTER_TYPE.FILE && i.isDisabled === false).map(counter => <MenuItem key={counter._id} value={counter._id}>
														{counter.prefix}
													</MenuItem>)}
										</TextField>
										<OutlinedInput disabled sx={{
                  width: "320px"
                }} placeholder={`${company?.fileAlias.singular ?? "File"} Prefix here`} />
										<TextField label={`${company?.fileAlias.singular ?? "File"}# Comment`} sx={{
                  width: "480px"
                }} defaultValue={till.createFileInput?.fileSuffixComment ?? ""} onBlur={e => {
                  const comment = e.target.value ?? "";
                  updateCreateFileInput({
                    fileSuffixComment: comment
                  });
                }} />
										<TextField select label="Invoice Prefix" sx={{
                  width: "320px"
                }} value={till.createFileInput?.counterConfigs?.invoice ? till.createFileInput?.counterConfigs?.invoice.counter : null} onChange={e => {
                  updateCreateFileInput({
                    counterConfigs: {
                      ...till.createFileInput?.counterConfigs,
                      invoice: {
                        counter: e.target.value
                      }
                    }
                  });
                }}>
											{counters.filter(i => i.type === COUNTER_TYPE.INVOICE && i.isDisabled === false).map(counter => <MenuItem key={counter._id} value={counter._id}>
														{counter.prefix}
													</MenuItem>)}
										</TextField>
										<OutlinedInput disabled sx={{
                  width: "320px"
                }} placeholder="Invoice prefix here" />
										<TextField label="Invoice# Comment" sx={{
                  width: "480px"
                }} defaultValue={till.createFileInput?.receiptSuffixComment ?? ""} onBlur={e => {
                  const comment = e.target.value ?? "";
                  updateCreateFileInput({
                    receiptSuffixComment: comment
                  });
                }} />
									</Stack>
								</Grid>
								<Grid xs={1} md={1}>
									<Stack justifyContent="flex-end" alignItems="center" direction="row" spacing={1}>
										<IconButton onClick={async () => await onClose()}>
											<SvgIcon>
												<XIcon />
											</SvgIcon>
										</IconButton>
									</Stack>
								</Grid>
							</Grid>
						</Box>
						<Box>
							<Stack spacing={2} direction="row">
								<DateTimePicker sx={{
              width: "25%"
            }} label="Issue Date" value={till.createFileInput?.issueDate ? new Date(till.createFileInput?.issueDate) : null} onChange={value => {
              if (!value) return;
              updateCreateFileInput({
                issueDate: value.toISOString()
              });
            }} />
								<DateTimePicker sx={{
              width: "25%"
            }} label="Due Date" value={till.createFileInput?.dueDate ? new Date(till.createFileInput?.dueDate) : null} onChange={value => {
              if (!value) return;
              updateCreateFileInput({
                dueDate: value.toISOString()
              });
            }} />
							</Stack>
						</Box>
						<Divider />
						<Grid container spacing={3}>
							<Grid container spacing={3} xs={12} md={6}>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Created by
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Stack direction="row" alignItems="center" spacing={1}>
										<Avatar src={me?.avatar ?? undefined} />
										<Typography variant="caption" color="text.secondary">
											{me?.firstName}
										</Typography>
									</Stack>
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Process paths
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									{!!till.createFileInput?.processPaths?.length && <Timeline sx={{
                m: 0,
                py: 0
              }}>
											{till.createFileInput.processPaths.slice(0, 3).map((path, index, current) => {
                  const processData = processes.find(process => process._id === path.process);
                  if (!processData) {
                    return null;
                  }
                  return <TimelineItem key={index} sx={{
                    "::before": {
                      content: "unset"
                    }
                  }}>
															<TimelineSeparator>
																<TimelineDot color={index === 0 ? "primary" : "grey"} />
																{index < 2 && !!current[index + 1] && <TimelineConnector />}
																{index === 2 && !!till.createFileInput!.processPaths[3] && <TimelineConnector />}
															</TimelineSeparator>
															<TimelineContent>
																{processData.name}
															</TimelineContent>
														</TimelineItem>;
                })}
										</Timeline>}

									<Button startIcon={<SvgIcon>
												<Route />
											</SvgIcon>} onClick={openProcessPathsDialog}>
										Define process paths
									</Button>
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Assignees
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Autocomplete multiple fullWidth disableCloseOnSelect options={users} value={users.filter(user => till.createFileInput?.assignees?.includes(user._id))} isOptionEqualToValue={(option, value) => option._id === value._id} getOptionLabel={option => option.firstName} onChange={(_, values) => {
                updateCreateFileInput({
                  assignees: values.map(user => user._id)
                });
              }} renderInput={params => <TextField {...params} label="Users" />} renderTags={(value, getTagProps) => value.map((value, index) => <Chip {...getTagProps({
                index
              })} key={value._id} label={value.firstName} avatar={<Avatar src={value.avatar ?? undefined} />} />)} renderOption={(props, option) => <li {...props}>
												<Avatar src={option.avatar ?? undefined} />
												&nbsp;{option.firstName}
											</li>} />
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Client
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Autocomplete fullWidth options={clients} renderOption={(props, option, {
                inputValue
              }) => {
                const label = `${option.companyIssuedId}-${option.name}`;
                const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
                const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
                return <li {...props} key={option._id} style={{
                  whiteSpace: "pre-wrap"
                }}>
													{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                    fontWeight: 800,
                    bgcolor: "primary.main"
                  } : {}}>
															{part}
														</Box>)}
												</li>;
              }} value={clients.find(client => client._id === till.createFileInput?.client)} isOptionEqualToValue={(option, value) => option._id === (value?._id ?? undefined)} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
                inputValue
              }) => {
                const input = inputValue.toLowerCase();
                const matchOptions = options.filter(o => {
                  const label = `${o.companyIssuedId}-${o.name}`;
                  const customFields = o.customFields.map(c => c.textValue).join(":");
                  const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
                  const matchLabel = label.toLowerCase().includes(input);
                  const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
                  return matchLabel || matchSearch;
                });
                const matches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return label.toLowerCase().includes(input.toLowerCase());
                });
                const nonMatches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return !label.toLowerCase().includes(input.toLowerCase());
                });
                return [...matches, ...nonMatches];
              }} onChange={(_, value) => updateCreateFileInput({
                client: value?._id
              })} onInput={e => {
                const event = e.nativeEvent as InputEvent;
                const input = e.target as any;
                if (event.inputType === "insertFromPaste") input.value = input.value.trim();
              }} onInputChange={(_, value, reason) => {
                if (reason === "input") setSearchClient(value);
              }} renderInput={params => <TextField {...params} label="Clients" error={company?.isCreateFileWithoutClient ? false : !till.createFileInput?.client} helperText={company?.isCreateFileWithoutClient ? null : till.createFileInput?.client ? null : "Required"} />} />
								</Grid>
								<Grid xs={12} sm={12}>
									<Button startIcon={<SvgIcon>
												<PlusIcon />
											</SvgIcon>} sx={{
                float: "right"
              }} variant="contained" onClick={async e => {
                e.stopPropagation();
                const newRouter = router;
                newRouter.pathname = paths.clients.create;
                newRouter.query = {
                  redirect: router.asPath,
                  clientName: searchClient
                };
                newRouter.query = {
                  ...router.query
                };
                await router.push(newRouter);
              }}>
										Create
									</Button>
								</Grid>
								<Grid xs={12} sm={4}>
									<Typography color="text.secondary" variant="caption">
										Agent
									</Typography>
								</Grid>
								<Grid xs={12} sm={8}>
									<Autocomplete fullWidth options={agents} renderOption={(props, option, {
                inputValue
              }) => {
                const label = `${option.companyIssuedId}-${option.name}`;
                const matches = label.toLowerCase().indexOf(inputValue.toLowerCase());
                const parts = matches !== -1 ? [label.substring(0, matches), label.substring(matches, matches + inputValue.length), label.substring(matches + inputValue.length)] : [label];
                return <li {...props} key={option._id} style={{
                  whiteSpace: "pre-wrap"
                }}>
													{parts.map((part, index) => <Box key={index} component="span" sx={part.toLowerCase() === inputValue.toLowerCase() ? {
                    fontWeight: 800,
                    bgcolor: "primary.main"
                  } : {}}>
															{part}
														</Box>)}
												</li>;
              }} value={agents.find(agent => agent._id === till.createFileInput?.agent)} isOptionEqualToValue={(option, value) => option._id === value._id} getOptionLabel={option => `${option.companyIssuedId}-${option.name}`} filterOptions={(options, {
                inputValue
              }) => {
                const input = inputValue.toLowerCase();
                const matchOptions = options.filter(o => {
                  const label = `${o.companyIssuedId}-${o.name}`;
                  const customFields = o.customFields.map(c => c.textValue).join(":");
                  const contacts = (o.contacts ?? []).map(contact => `${contact.email}:${contact.firstName}:${contact.lastName}:${contact.mobile}:${contact.telephone}:${contact.customFields.filter(custom => custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_SINGLE || custom._customField.type === CUSTOM_FIELD_TYPE.TEXT_MULTI).map(c => c.textValue).join(":")}`);
                  const matchLabel = label.toLowerCase().includes(input);
                  const matchSearch = `${customFields}:${contacts}`.toLowerCase().includes(input);
                  return matchLabel || matchSearch;
                });
                const matches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return label.toLowerCase().includes(input.toLowerCase());
                });
                const nonMatches = matchOptions.filter(option => {
                  const label = `${option.companyIssuedId}-${option.name}`;
                  return !label.toLowerCase().includes(input.toLowerCase());
                });
                return [...matches, ...nonMatches];
              }} onChange={(_, value) => updateCreateFileInput({
                agent: value?._id
              })} onInput={e => {
                const event = e.nativeEvent as InputEvent;
                const input = e.target as any;
                if (event.inputType === "insertFromPaste") input.value = input.value.trim();
              }} renderInput={params => <TextField {...params} label="Agents" error={company?.isCreateFileWithoutAgent ? false : !till.createFileInput?.agent} helperText={company?.isCreateFileWithoutAgent ? null : till.createFileInput?.agent ? null : "Required"} />} />
								</Grid>
								{customFields.map(field => [<Grid xs={12} sm={4} key={`${field._id}-label`}>
										<Typography color="text.secondary" variant="caption">
											{field.name}
										</Typography>
									</Grid>, <Grid xs={12} sm={8} key={`${field._id}-input`}>
										<CustomFieldRenderer field={field} value={customFieldValues[field._id]} onChange={value => {
                const updatedCustomFieldValues = {
                  ...customFieldValues,
                  [value.customField]: value
                };
                setCustomFieldValues(updatedCustomFieldValues);
              }} />
									</Grid>])}
								{(me?.role === ROLE.SUPER_ADMIN || company?.isCustomFieldEnabled) && <Grid xs={12} textAlign="right">
										<Button startIcon={<BarChartSquarePlus />} onClick={openCustomFieldsDialog}>
											Manage custom fields
										</Button>
									</Grid>}
							</Grid>
							<Grid container spacing={3} xs={12} md={6} flexDirection="column">
								<Grid xs={12} textAlign="left">
									<Button onClick={() => {
                setTillIsOpen(true);
              }} startIcon={<SvgIcon>
												<Pencil01 />
											</SvgIcon>}>
										Add Products
									</Button>
								</Grid>
								<Grid xs={12}>
									<ProductsTableSummary items={productsTableItems} itemsDisplay={fileItemsDisplay} subtotal={createFileInputSummary.subtotal} additionalFee={createFileInputSummary.additionalFeeAmount} fixDiscount={createFileInputSummary.fixDiscountAmount} grandTotal={createFileInputSummary.grossAmount} />
								</Grid>
							</Grid>
						</Grid>
						<LoadingButton startIcon={<SvgIcon>
									<PostAdd />
								</SvgIcon>} sx={{
          position: "absolute",
          m: 5,
          bottom: 0,
          right: 0
        }} loading={createFile.isLoading} variant="contained" onClick={() => handleCreateFileInColumn(appQuery.processId ?? "")}>
							Create
						</LoadingButton>
					</>}
			</Stack>
		</Dialog>;
};